import { mounted } from 'component-utils';
import { parseNumber, round } from 'intl-utils';
import { fetch, getName } from 'utils';

onLoad();

export function onLoad() {
    const container = document.getElementById('paypal-express-container');

    if (container == null)
        return;

    // Note: You must make sure the container is mounted, otherwise `container.$data` will return undefined.
    mounted(container, () => {
        const sessionData = JSON.parse(container.dataset.sessionData);

        let deliveryAddressCountryCode = container.$data.deliveryAddress?.countryCode;
        let deliveryAddressPostcode = container.$data.deliveryAddress?.postcode;

        paypal.Buttons({
            style: {
                height: 44,
                disableMaxWidth: true
            },
            createOrder: async (data, actions) => {
                // Initially tried dispatching an event wrapped in a promise and resolving it within the event listener but then you must make sure the event listener is defined which isn't always the case.
                if (typeof window.initExpressCheckout === 'function' && await window.initExpressCheckout(container) === false)
                    return;

                // Make sure a default delivery method is selected. If there is a delivery method id set against the cart then make sure it is in the available delivery methods.
                if (container.$data.cart.isDeliveryRequired && (container.$data.cart.deliveryMethodId == null || !container.$data.cart.deliveryMethods.some(m => m.id == container.$data.cart.deliveryMethodId)) && container.$data.cart.deliveryMethods.length > 0)
                    await setDeliveryMethod(container.$data.cart.deliveryMethods[0].id);

                return actions.order.create({
                    intent: !sessionData['IsDeferred'] ? 'CAPTURE' : 'AUTHORIZE',
                    purchase_units: [{
                        amount: {
                            value: parseNumber(container.$data.cart.grandTotal),
                            breakdown: {
                                item_total: {
                                    currency_code: sessionData['Currency'],
                                    value: round(parseNumber(container.$data.cart.grandTotal) - parseNumber(container.$data.cart.delivery) - parseNumber(container.$data.cart.deliveryTax), 2) // Note: We must call round since in JavaScript you get small imprecisions when performing arithmetic. For example: console.log(0.2 + 0.1); // Output: 0.30000000000000004.
                                },
                                shipping: {
                                    currency_code: sessionData['Currency'],
                                    value: round(parseNumber(container.$data.cart.delivery) + parseNumber(container.$data.cart.deliveryTax), 2)
                                }
                            }
                        },
                        shipping: container.$data.cart.isDeliveryRequired ? {
                            options: container.$data.cart.deliveryMethods.map(deliveryMethod => ({
                                id: deliveryMethod.id.toString(),
                                label: deliveryMethod.displayName ?? deliveryMethod.name,
                                type: deliveryMethod.isAddressRequired ? 'SHIPPING' : 'PICKUP',
                                selected: deliveryMethod.isSelected,
                                amount: {
                                    currency_code: sessionData['Currency'],
                                    value: round(parseNumber(deliveryMethod.amount) + parseNumber(deliveryMethod.amountTax), 2)
                                }
                            }))
                        } : null
                    }],
                    application_context: !container.$data.cart.isDeliveryRequired ? {
                        shipping_preference: 'NO_SHIPPING'
                    } : null
                });
            },
            onShippingAddressChange: async (data, actions) => {
                // Make sure the delivery method is valid for this address.
                if (!(await isDeliveryMethodValid(container.$data.cart.deliveryMethodId)))
                    return actions.reject();

                // Set the delivery address.
                deliveryAddressCountryCode = data.shippingAddress.countryCode;
                deliveryAddressPostcode = data.shippingAddress.postalCode;
            },
            onShippingOptionsChange: async (data, actions) => {
                // Make sure the delivery method is valid.
                if (!(await isDeliveryMethodValid(data.selectedShippingOption.id)))
                    return actions.reject();

                // Set the delivery method.
                await setDeliveryMethod(data.selectedShippingOption.id);
            },
            onApprove: async (data, actions) => {
                let details;

                if (!sessionData['IsDeferred'])
                    details = await actions.order.capture();
                else
                    details = await actions.order.authorize();

                const tempData = new FormData();
                tempData.append('Data', JSON.stringify(details));

                fetch(`/api/payment?id=${data.orderID}`, {
                    method: 'POST',
                    body: tempData
                });

                const formData = new FormData();
                formData.append('Contact.Name.FirstName', details.payer.name.given_name);
                formData.append('Contact.Name.LastName', details.payer.name.surname);
                formData.append('Contact.Email', details.payer.email_address);
                formData.append('IsDeliveryRequired', container.$data.cart.isDeliveryRequired);
                formData.append('Delivery.DeliveryMethodId', container.$data.cart.deliveryMethodId ?? '');

                if (container.$data.cart.isDeliveryAddressRequired) {
                    const shipping = details.purchase_units[0].shipping;
                    const deliveryName = getName(shipping.name.full_name);
                    const deliveryCountryResponse = await fetch(`/api/countries/${shipping.address.country_code}`);
                    const deliveryCountryId = (await deliveryCountryResponse.json()).id;

                    formData.append('Delivery.IsNewDeliveryAddressRequired', true);
                    formData.append('Delivery.NewDeliveryAddress.Name.FirstName', deliveryName.firstName);
                    formData.append('Delivery.NewDeliveryAddress.Name.LastName', deliveryName.lastName ?? '');
                    formData.append('Delivery.NewDeliveryAddress.AddressLine1', shipping.address.address_line_1);
                    formData.append('Delivery.NewDeliveryAddress.AddressLine2', shipping.address.address_line_2 ?? '');
                    formData.append('Delivery.NewDeliveryAddress.Town', shipping.address.admin_area_2);
                    formData.append('Delivery.NewDeliveryAddress.County', shipping.address.admin_area_1 ?? '');
                    formData.append('Delivery.NewDeliveryAddress.CountryId', deliveryCountryId);
                    formData.append('Delivery.NewDeliveryAddress.Postcode', shipping.address.postal_code);
                }

                formData.append('Payment.PaymentMethodId', container.dataset.paymentMethodId);
                formData.append('Payment.Token', data.orderID);
                formData.append('Terms', true);

                // Create the order.
                await fetch(`/checkout?sessionId=${container.$data.cart.sessionId}&isBillingAddressRequired=false&isCardDetailsRequired=false`, {
                    method: 'POST',
                    body: formData
                });

                // Confirm the order. This will update the processor reference number with the capture/authorize id.
                const response = await fetch(`/api/orders/${data.orderID}/execute`, {
                    method: 'POST'
                });

                window.location.href = response.ok ? `/checkout/complete?sessionId=${container.$data.cart.sessionId}` : `/checkout/failed?sessionId=${container.$data.cart.sessionId}`;
            },
            onError: error => {
                console.warn(error);
            }
        }).render('#paypal-express-button');

        async function isDeliveryMethodValid(id) {
            const response = await fetch(`/api/cart/validate-delivery-method?id=${id}&sessionId=${container.$data.cart.sessionId}&deliveryAddressCountryCode=${deliveryAddressCountryCode ? encodeURIComponent(deliveryAddressCountryCode) : ''}&deliveryAddressPostcode=${deliveryAddressPostcode ? encodeURIComponent(deliveryAddressPostcode) : ''}`, {
                method: 'POST'
            });

            return response.ok;
        }

        async function setDeliveryMethod(id) {
            const response = await fetch(`/api/cart/set-delivery-method?id=${id}&sessionId=${container.$data.cart.sessionId}&deliveryAddressCountryCode=${deliveryAddressCountryCode ? encodeURIComponent(deliveryAddressCountryCode) : ''}&deliveryAddressPostcode=${deliveryAddressPostcode ? encodeURIComponent(deliveryAddressPostcode) : ''}`, {
                method: 'POST'
            });
            const data = await response.json();

            container.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: data }));
        }
    });
}